import useSWR from "swr";
import {api, HydraData} from "../api/apiProvider";
import urls from "../api/urls";
import {useEffect, useState} from "react";
import {MediaObject, Model as ApiModel, ModelFileType} from "../api/types";
import {UploadFile} from "antd";

export interface Model extends ApiModel {
  fileTypeName: string,
  fileTypeSid: string,
  fileUrl: string,
  fileName: string,
  type: string,
  fileBlob?: UploadFile[],
}

export function useModels(briefId: number) {
  const [models, setModels] = useState<ApiModel[]>([])

  const {
    data: apiModels,
    mutate: mutateApiModels,
  } = useSWR<HydraData>(`${urls.models}?brief.id=${briefId}`, api.get, {
    revalidateOnFocus: false
  })

  const {
    data: modelFileTypes,
    mutate: mutateModelsFileTypes,
  } = useSWR<HydraData>(urls.modelsFileTypes, api.get, {
    revalidateOnFocus: false
  })

  const getModelFilesUrl = (): string | null => {
    if (!apiModels || !apiModels.data || apiModels.data.length === 0) {
      return null
    }

    const filesIds = apiModels.data
    .filter((apiModel: Model) => apiModel.file !== null)
    .map((apiModel: Model) => apiModel.file ? apiModel.file.split('/').pop() : '')

    if (filesIds.length === 0) {
      return null
    }

    return `${urls.mediaObjects}?${filesIds.map((fileId: string) => `id[]=${fileId}`).join('&')}`
  }

  const {
    data: modelFiles,
    mutate: mutateModelFiles,
  } = useSWR<HydraData>(getModelFilesUrl(), api.get, {
    revalidateOnFocus: false
  })

  useEffect(() => {
    if (apiModels && apiModels.data && modelFileTypes && modelFileTypes.data) {
      const models: Model[] = []

      for (let apiModel of apiModels.data) {
        const fileType = modelFileTypes.data.find((modelFileType: ModelFileType) => modelFileType['@id'] === apiModel.fileType)
        const file: MediaObject = (modelFiles && modelFiles.data && apiModel.file)
          ? modelFiles.data.find((file: MediaObject) => file['@id'] === apiModel.file)
          : null

        models.push({
          '@id': apiModel['@id'],
          brief: apiModel.brief,
          file: apiModel.file,
          fileType: apiModel.fileType,
          id: apiModel.id,
          fileTypeName: fileType ? fileType.name : '',
          fileTypeSid: fileType ? fileType.sid : '',
          fileUrl: file ? file.contentUrl : '',
          fileName: file && file.originalName ? file.originalName : '',
          fileBlob: undefined,
          type: file?.originalName?.split('.').pop() || ''
        })
      }

      setModels(models)
    }
  }, [apiModels, modelFileTypes, modelFiles])

  const addFileToModel = async (modelId: number, fileIri: string) => {
    return await api.patch(`${urls.models}/${modelId}`, {
      arg: {
        file: fileIri,
      }
    })
  }

  const deleteFileFromModel = async (modelId: number) => {
    return await api.patch(`${urls.models}/${modelId}`, {
      arg: {
        file: null,
      }
    })
  }

  return {models, deleteFileFromModel, getModels: mutateApiModels, addFileToModel}
}
